import { defineStore } from 'pinia';
import { notificationCenterService } from '@/ShopManagement/services/notificationCenterService.js';
import { NOTIFICATIONS_UPDATED_EVENT, getNotificationInQueue } from '@/Modules/WebSockets.js';
import { notificationLabel } from '@/Constants/NotificationLabels.js';

/**
 * Notification centre store — the reactive source of truth for the top-bar
 * bell. Actions delegate all I/O to notificationCenterService and own the
 * orchestration: seed the badge from Inertia props, subscribe to the user's own
 * live channel, and refetch when a notification arrives.
 */

// Private, non-reactive bookkeeping — one subscription per app session.
let channelName = null;
let refreshTimer = null;
let legacyListener = null;

/**
 * The legacy bell is websocket-only and has no per-item read state — its badge
 * has always been "how many items are in the queue". Normalise its items onto
 * the same shape as BayHive rows so one list can render both.
 */
function normalizeLegacy(notification) {
  const body = [notification.message, notification.invoice ? `RO# ${notification.invoice}` : '']
    .filter(Boolean)
    .join(' · ');

  return {
    key: `legacy:${notification.id}|${notification.notify}`,
    source: 'legacy',
    id: notification.id,
    notify: notification.notify,
    letter: notificationLabel(notification.notify),
    title: notification.name,
    body,
    icon: null,
    tone: 'info',
    url: notification.href,
    createdAt: notification.date_time,
    unread: true,
  };
}

function normalizeBayHive(notification) {
  return {
    key: `bayhive:${notification.id}`,
    source: 'bayhive',
    id: notification.id,
    letter: null,
    title: notification.title,
    body: notification.body,
    icon: notification.icon,
    tone: notification.tone,
    url: notification.url,
    createdAt: notification.createdAt,
    unread: notification.unread,
  };
}

const sortableTime = (value) => {
  const parsed = Date.parse(value);

  return Number.isNaN(parsed) ? 0 : parsed;
};

export const useNotificationCenterStore = defineStore('notificationCenter', {
  state: () => ({
    items: [],
    legacyItems: [],
    unreadCount: 0,
    // False until the list itself has been fetched. The badge can be correct
    // from props long before the dropdown has ever been opened.
    loaded: false,
    loading: false,
    error: '',
  }),

  getters: {
    /**
     * Both feeds in one time-sorted list. Legacy items carry no read state, so
     * they always count toward the badge — matching the legacy bell's own
     * behaviour, where the badge was simply the queue length.
     */
    mergedItems(state) {
      return [...state.items.map(normalizeBayHive), ...state.legacyItems.map(normalizeLegacy)].sort(
        (a, b) => sortableTime(b.createdAt) - sortableTime(a.createdAt),
      );
    },

    badgeCount(state) {
      return state.unreadCount + state.legacyItems.length;
    },
  },

  actions: {
    applyState(payload) {
      this.items = payload.items ?? [];
      this.unreadCount = payload.unreadCount ?? 0;
      this.loaded = true;
      this.error = '';
    },

    /**
     * Seed the badge from the shared Inertia prop and open the live
     * subscription. The component passes both in from `$page.props` — the
     * router's page object is not reliably readable from outside a component.
     * Safe to call on every page: both halves are idempotent.
     */
    hydrateFromProps({ payload = null, userId = null } = {}) {
      if (payload && typeof payload.unreadCount === 'number') {
        this.unreadCount = payload.unreadCount;
      }

      this.ensureLiveSubscription(userId);
    },

    /**
     * Bridge the legacy AutoFlow websocket feed into this store, mirroring what
     * Globals/Notifications.vue does for the AutoFlow layout. Independent of
     * the Echo subscription above: either half can be unavailable and the other
     * still works.
     */
    startLegacyBridge({ wssUrl = null, masterConfig = null, user = null, toggles = null } = {}) {
      if (legacyListener || !wssUrl || !masterConfig || !user) return;

      this.ingestLegacy(globalThis.window?.notifications);

      legacyListener = (event) =>
        this.ingestLegacy(event.detail ?? globalThis.window?.notifications);
      globalThis.window?.addEventListener(NOTIFICATIONS_UPDATED_EVENT, legacyListener);

      getNotificationInQueue(wssUrl, masterConfig, user, {
        ...toggles,
        notifications_retention: toggles?.notifications_retention ?? 7,
      });
    },

    ingestLegacy(notifications) {
      this.legacyItems = Array.isArray(notifications) ? [...notifications] : [];
    },

    teardownLegacyBridge() {
      if (legacyListener) {
        globalThis.window?.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, legacyListener);
        legacyListener = null;
      }
    },

    ensureLiveSubscription(userId) {
      if (!userId) return;

      const name = `bayhive.notifications.${userId}`;
      if (channelName === name) return;

      if (channelName) notificationCenterService.leave(channelName);
      channelName = name;
      notificationCenterService.subscribe(name, (event) => {
        // The broadcast carries only the count, so the badge can move
        // immediately; the list is refetched so the server re-applies paging.
        if (typeof event?.unreadCount === 'number') {
          this.unreadCount = event.unreadCount;
        }
        this.scheduleRefresh();
      });
    },

    teardownLiveSubscription() {
      if (channelName) notificationCenterService.leave(channelName);
      channelName = null;
      if (refreshTimer) {
        globalThis.clearTimeout(refreshTimer);
        refreshTimer = null;
      }
    },

    // A burst of notifications (one review fanned out to several people, or
    // several reviews at once) should cost one refetch, not one each.
    scheduleRefresh() {
      if (refreshTimer) globalThis.clearTimeout(refreshTimer);
      refreshTimer = globalThis.setTimeout(() => {
        refreshTimer = null;
        if (this.loaded) this.load();
      }, 150);
    },

    async load() {
      this.loading = true;
      this.error = '';

      try {
        this.applyState(await notificationCenterService.load());
      } catch (error) {
        this.error = error.message || 'Notifications could not be loaded.';
      } finally {
        this.loading = false;
      }
    },

    async markRead(notificationId) {
      try {
        this.applyState(await notificationCenterService.markRead(notificationId));
      } catch (error) {
        this.error = error.message || 'That notification could not be updated.';
      }
    },

    async markLegacyRead(notification) {
      try {
        await notificationCenterService.markLegacyRead(notification.id, notification.notify);
      } catch (error) {
        this.error = error.message || 'That notification could not be updated.';
      }
    },

    /**
     * Clears both halves. They are independent calls so a websocket-side
     * failure cannot stop the BayHive inbox from being marked read.
     */
    async markAllRead() {
      const results = await Promise.allSettled([
        notificationCenterService.markAllRead(),
        this.legacyItems.length ? notificationCenterService.clearLegacyAll() : Promise.resolve(),
      ]);

      if (results[0].status === 'fulfilled') {
        this.applyState(results[0].value);
      } else {
        this.error = results[0].reason?.message || 'Notifications could not be updated.';
      }

      if (results[1].status === 'fulfilled') {
        this.legacyItems = [];
        if (globalThis.window) globalThis.window.notifications = [];
      }
    },
  },
});
